/**
 * Utility helper functions used throughout the application
 * Common operations for data manipulation, formatting, and validation
 */

import { parseISO, format, formatDistanceToNow, isValid } from 'date-fns'
import DOMPurify from 'dompurify'

/**
 * Format currency amount with proper symbol and decimals
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 'N/A'
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch (error) {
    console.error('Currency formatting error:', error)
    return `${currency} ${amount.toFixed(2)}`
  }
}

/**
 * Format percentage with proper decimal places
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A'
  }

  return `${value.toFixed(decimals)}%`
}

/**
 * Format date with consistent formatting using date-fns
 * @param {string|Date} date - Date string or Date object
 * @param {string} formatStr - Format string (default: 'MMM dd, yyyy')
 * @returns {string} Formatted date string or 'N/A' for invalid dates
 */
export const formatDate = (date, formatStr = 'MMM dd, yyyy') => {
  if (!date) return 'N/A'
  
  try {
    // Handle both Date objects and date strings
    let normalized
    if (date instanceof Date) {
      // If it's already a Date object, use it directly
      normalized = date
    } else {
      // If it's a string, parse it with parseISO
      normalized = parseISO(date)
    }
    
    return isValid(normalized) ? format(normalized, formatStr) : 'N/A'
  } catch (error) {
    console.error('Date formatting error:', error)
    return 'N/A'
  }
}

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago") using date-fns
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Relative time string or 'N/A' for invalid dates
 */
export const formatRelativeTime = (date) => {
  if (!date) return 'N/A'
  
  try {
    const normalized = parseISO(date)
    return isValid(normalized) ? formatDistanceToNow(normalized, { addSuffix: true }) : 'N/A'
  } catch (error) {
    console.error('Relative time formatting error:', error)
    return 'N/A'
  }
}

/**
 * Alias for formatRelativeTime for backward compatibility
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Relative time string or 'N/A' for invalid dates
 */
export const getTimeAgo = formatRelativeTime

/**
 * Truncate text to specified length with ellipsis
 */
export const truncateText = (text, maxLength = 100, suffix = '...') => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength) + suffix
}

/**
 * Capitalize first letter of string
 */
export const capitalize = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Convert string to title case
 */
export const toTitleCase = (str) => {
  if (!str) return ''
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  )
}

/**
 * Generate random ID string
 */
export const generateId = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * Debounce function to limit function calls
 */
export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Throttle function to limit function execution frequency
 */
export const throttle = (func, limit) => {
  let inThrottle
  return function() {
    const args = arguments
    const context = this
    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * Deep clone object (simple implementation)
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime())
  if (obj instanceof Array) return obj.map(item => deepClone(item))
  if (typeof obj === 'object') {
    const clonedObj = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  if (!email) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate phone number format (basic validation)
 */
export const isValidPhone = (phone) => {
  if (!phone) return false
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''))
}

/**
 * Get initials from name
 */
export const getInitials = (name) => {
  if (!name) return ''
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Convert bytes to human readable format
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * Sleep function for async operations
 */
export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Format quarter data to readable format (e.g., "Q1 2024")
 * Handles various input formats and converts to consistent quarter display
 * @param {string} quarterData - Raw quarter data from API
 * @returns {string} Formatted quarter string
 */
export const formatQuarter = (quarterData) => {
  if (!quarterData) return 'Unknown Quarter'
  
  // If already in "Q1 2024" format, return as is
  if (/^Q[1-4]\s+\d{4}$/.test(quarterData)) {
    return quarterData
  }
  
  // If it's a date string, extract quarter and year using date-fns
  if (quarterData.includes('-') || quarterData.includes('/')) {
    try {
      const normalized = parseISO(quarterData)
      if (isValid(normalized)) {
        const year = normalized.getFullYear()
        const month = normalized.getMonth() + 1 // 0-based to 1-based
        const quarter = Math.ceil(month / 3)
        return `Q${quarter} ${year}`
      }
    } catch (error) {
      console.warn('Error parsing quarter date:', quarterData, error)
    }
  }
  
  // If it's a year-only string, assume Q1
  if (/^\d{4}$/.test(quarterData)) {
    return `Q1 ${quarterData}`
  }
  
  // If it contains year and quarter info, try to extract
  const yearMatch = quarterData.match(/(\d{4})/)
  const quarterMatch = quarterData.match(/Q?(\d)/i)
  
  if (yearMatch && quarterMatch) {
    return `Q${quarterMatch[1]} ${yearMatch[1]}`
  }
  
  // Fallback: return original data
  return quarterData
}

/**
 * Convert array of objects to CSV format
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Array of column definitions with key and label
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) return ''
  
  // Create header row
  const headers = columns.map(col => col.label).join(',')
  
  // Create data rows
  const rows = data.map(row => {
    return columns.map(col => {
      let value = row[col.key] || ''
      
      // Apply custom render function if available
      if (col.render) {
        value = col.render(value, row)
      }
      
      // Escape CSV values (handle commas, quotes, newlines)
      if (typeof value === 'string') {
        // If value contains comma, quote, or newline, wrap in quotes and escape existing quotes
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value.replace(/"/g, '""')}"`
        }
      }
      
      return value
    }).join(',')
  })
  
  return [headers, ...rows].join('\n')
}

/**
 * Download data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions
 * @param {string} filename - Name of the file (without extension)
 */
export const downloadCSV = (data, columns, filename = 'export') => {
  const csvContent = convertToCSV(data, columns)
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `${filename}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

/**
 * Safely sanitize HTML content using DOMPurify
 * Removes potentially dangerous scripts, events, and attributes
 * @param {string} html - Raw HTML string to sanitize
 * @param {Object} options - DOMPurify configuration options
 * @returns {string} Sanitized HTML string safe for rendering
 */
export const sanitizeHTML = (html, options = {}) => {
  if (!html || typeof html !== 'string') {
    return ''
  }

  try {
    // Default configuration for maximum security
    const defaultOptions = {
      // Allow common HTML tags for formatting
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'b', 'i', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'a', 'img', 'div', 'span', 'table', 'tr', 'td', 'th', 'tbody', 'thead'
      ],
      // Allow safe attributes
      ALLOWED_ATTR: [
        'href', 'title', 'alt', 'src', 'width', 'height', 'class', 'id', 'style',
        'colspan', 'rowspan', 'align', 'valign'
      ],
      // Remove all script tags and event handlers
      FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
      // Keep relative URLs but sanitize absolute ones
      ALLOW_DATA_ATTR: false,
      // Remove all comments
      ALLOW_UNKNOWN_PROTOCOLS: false,
      // Sanitize CSS in style attributes
      SANITIZE_DOM: false,
      // Remove empty elements
      KEEP_CONTENT: true,
      // Return document fragment for better performance
      RETURN_DOM_FRAGMENT: false,
      // Return DOM node for React dangerouslySetInnerHTML
      RETURN_DOM: false,
      // Return trusted HTML
      RETURN_TRUSTED_TYPE: false
    }

    // Merge user options with defaults
    const config = { ...defaultOptions, ...options }
    
    // Sanitize the HTML
    const cleanHTML = DOMPurify.sanitize(html, config)
    
    return cleanHTML
  } catch (error) {
    console.error('HTML sanitization error:', error)
    // Return escaped text as fallback
    return html.replace(/[<>&"']/g, (match) => {
      const escapeMap = {
        '<': '&lt;',
        '>': '&gt;',
        '&': '&amp;',
        '"': '&quot;',
        "'": '&#x27;'
      }
      return escapeMap[match]
    })
  }
}

/**
 * Create a React component for safely rendering sanitized HTML
 * @param {string} html - Raw HTML string to sanitize and render
 * @param {Object} sanitizeOptions - DOMPurify configuration options
 * @returns {Object} React props object for dangerouslySetInnerHTML
 */
export const createSafeHTMLProps = (html, sanitizeOptions = {}) => {
  const sanitizedHTML = sanitizeHTML(html, sanitizeOptions)
  
  return {
    dangerouslySetInnerHTML: {
      __html: sanitizedHTML
    }
  }
}

/**
 * Create HTML content for iframe with proper styling and security
 * @param {string} html - Raw HTML content to render
 * @param {Object} options - Configuration options for iframe content
 * @returns {string} Complete HTML document for iframe
 */
export const createIframeHTML = (html, options = {}) => {
  const {
    title = 'Portfolio Company Update',
    allowImages = true,
    allowLinks = true,
    maxWidth = '100%'
  } = options

  // Basic sanitization - remove scripts and dangerous elements
  const sanitizedHTML = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')

  return `
<!DOCTYPE html>
<html lang="en">
  ${sanitizedHTML}
</html>
  `.trim()
}
